import { Box, MenuItem, Typography } from "@mui/material";
import {
  DepositDetailWrapper,
  DepositTextWrapper,
  StyledDepositDrawer,
  WalletAddressContainer,
} from "./styled";
import { CircleButton } from "components/CircleButton";
import { ReactComponent as TonTokenIcon } from "../../assets/icons/token-ton.svg";
import { ReactComponent as SolTokenIcon } from "../../assets/icons/token-sol.svg";
import { ReactComponent as EthTokenIcon } from "../../assets/icons/token-eth.svg";
import { ReactComponent as BaseTokenIcon } from "../../assets/icons/token-base.svg";
import { ReactComponent as BeraTokenIcon } from "../../assets/icons/token-bera.svg";
// import { ReactComponent as BinanceTokenIcon } from "../../assets/icons/token-bnb.svg";
import { ReactComponent as GroupIcon } from "../../assets/icons/deposit-money.svg";
import SolanaIcon from "../../assets/icons/chains/solana.png";
import EthereumIcon from "../../assets/icons/chains/ethereum.png";
import TonIcon from "../../assets/icons/chains/ton.png";
import BaseIcon from "../../assets/icons/chains/base.png";
import BeraIcon from "../../assets/icons/chains/bera.png";
import BinanceIcon from "../../assets/icons/chains/bsc.png";
import { RetroButton } from "components/RetroButton";
import { copyToClipboard } from "utils";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { WalletAddresses } from "store/user-store-v2";
import { Close } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { Select } from "components/Select";
import { Chain, ChainSymbol } from "chainConfigs";

interface TokenConfig {
  symbol: string;
  networkName: string;
  walletName: keyof WalletAddresses;
  icon: JSX.Element;
}

const configs: Partial<Record<ChainSymbol, TokenConfig>> = {
  TON: {
    symbol: "TON",
    networkName: "The Open",
    walletName: "tonAddress",
    icon: <TonTokenIcon />,
  },
  SOL: {
    symbol: "SOL",
    networkName: "SOLANA",
    walletName: "solAddress",
    icon: <SolTokenIcon />,
  },
  ETH: {
    symbol: "ETH",
    networkName: "ETHEREUM",
    walletName: "ethAddress",
    icon: <EthTokenIcon />,
  },
  BASE: {
    symbol: "ETH",
    networkName: "BASE",
    walletName: "ethAddress", // Pending API update to support Base wallet (turnkey)
    icon: <BaseTokenIcon />,
  },
};

export const DepositModal = ({
  tokenType,
  isOpen,
  close,
  onCopySuccess,
}: {
  tokenType: ChainSymbol;
  isOpen: boolean;
  close: () => void;
  onCopySuccess?: () => void;
}) => {
  const { walletAddresses } = useUserStoreV2();
  const config = configs[tokenType];

  const handleCopy = () => {
    if (!walletAddresses) return;
    const walletAddress = walletAddresses[config!.walletName as keyof WalletAddresses];
    if (walletAddress !== undefined) {
      const success = copyToClipboard(walletAddress);
      if (success) {
        onCopySuccess?.();
      }
    }
  };

  return (
    <StyledDepositDrawer anchor={"bottom"} open={isOpen} onClose={close}>
      <Box
        sx={{
          marginLeft: "auto",
          fontSize: "16px",
        }}>
        <CircleButton variant="white" onClick={close}>
          <Close fontSize="inherit" />
        </CircleButton>
      </Box>
      <DepositDetailWrapper>
        <Box>{configs[tokenType]!.icon}</Box>
        <Box>
          <Typography variant="display-md">DEPOSIT</Typography>
        </Box>
        <DepositTextWrapper>
          <Typography variant="body-lg" color="text.secondary">
            Sending only {configs[tokenType]!.symbol} on {config!.networkName} Network to this
            address, or you might lose your funds.
          </Typography>
        </DepositTextWrapper>
        <WalletAddressContainer>
          <Typography variant="body-md" color="text.primary">
            {walletAddresses ? walletAddresses[config!.walletName as keyof WalletAddresses] : ""}
          </Typography>
        </WalletAddressContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            maxWidth: "300px",
            marginX: "auto",
          }}>
          <RetroButton variant="primary" onClick={handleCopy}>
            <Typography variant="title-h3" sx={{ padding: "12px 21.5px" }}>
              COPY ADDRESS
            </Typography>
          </RetroButton>
        </Box>
      </DepositDetailWrapper>
    </StyledDepositDrawer>
  );
};

interface CrossChainConfig {
  id: string;
  symbol: string;
  networkName: React.ReactNode;
  walletName: keyof WalletAddresses;
}

const crossChainConfigs: Record<string, CrossChainConfig> = {
  // ton: {
  //   id: "ton",
  //   symbol: "TON",
  //   networkName: (
  //     <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
  //       <img src={TonIcon} alt="TON" width="25px" height="24px" />
  //       <Typography variant="body-md">The Open Network (TON)</Typography>
  //     </Box>
  //   ),
  //   walletName: "tonAddress",
  // },
  solana: {
    id: "solana",
    symbol: "SOL",
    networkName: (
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <img src={SolanaIcon} alt="SOL" width="25px" height="24px" />
        <Typography variant="body-md">Solana</Typography>
      </Box>
    ),
    walletName: "solAddress",
  },
  ethereum: {
    id: "ethereum",
    symbol: "ETH",
    networkName: (
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <img src={EthereumIcon} alt="ETH" width="25px" height="24px" />
        <Typography variant="body-md">Ethereum</Typography>
      </Box>
    ),
    walletName: "ethAddress",
  },
  base: {
    id: "base",
    symbol: "ETH(BASE)",
    networkName: (
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <img src={BaseIcon} alt="BASE" width="25px" height="24px" />
        <Typography variant="body-md">Base</Typography>
      </Box>
    ),
    walletName: "ethAddress",
  },
  berachain: {
    id: "berachain",
    symbol: "BERA",
    networkName: (
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <img src={BeraIcon} alt="BERA" width="25px" height="24px" />
        <Typography variant="body-md">Berachain</Typography>
      </Box>
    ),
    walletName: "ethAddress",
  },
  bsc: {
    id: "bsc",
    symbol: "BNB",
    networkName: (
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <img src={BinanceIcon} alt="BNB" width="25px" height="24px" />
        <Typography variant="body-md">Binance Smart Chain (BSC)</Typography>
      </Box>
    ),
    walletName: "ethAddress",
  },
};

export const CrossChainDepositModal = ({
  defaultChain = "ethereum",
  chains,
  isOpen,
  close,
  onCopySuccess,
}: {
  defaultChain: Chain;
  chains: Array<Chain>;
  isOpen: boolean;
  close: () => void;
  onCopySuccess?: () => void;
}) => {
  const [selectedChain, setSelectedChain] = useState<Chain>(defaultChain);
  const { walletAddresses } = useUserStoreV2();

  const sortedConfigs = useMemo(() => {
    const config = chains
      // Map to cross chain config
      .map((chains) => {
        return crossChainConfigs[chains];
      })
      // Sort by default token type
      .sort((a, b) => {
        const isAMatch = a.id === defaultChain ? 1 : -1;
        const isBMatch = b.id === defaultChain ? 1 : -1;

        return isAMatch - isBMatch;
      });
    return config;
  }, [chains, defaultChain]);

  const supporttedChainList = useMemo(() => {
    const result = sortedConfigs.map((config) => {
      return config.symbol;
    });
    return result;
  }, [sortedConfigs]);

  const handleCopy = () => {
    if (!walletAddresses) return;
    const walletAddress = walletAddresses[crossChainConfigs[selectedChain].walletName];
    if (walletAddress !== undefined) {
      const success = copyToClipboard(walletAddress);
      if (success) {
        onCopySuccess?.();
      }
    }
  };

  return (
    <StyledDepositDrawer anchor={"bottom"} open={isOpen} onClose={close}>
      <Box
        sx={{
          marginLeft: "auto",
          fontSize: "16px",
        }}>
        <CircleButton variant="white" onClick={close}>
          <Close fontSize="inherit" />
        </CircleButton>
      </Box>
      <DepositDetailWrapper>
        <Box>
          <GroupIcon height="140px" width="140px" />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            maxWidth: "495px",
            marginBottom: "16px",
            textAlign: "center",
          }}>
          <Typography variant="title-h1">Deposit to your wallet</Typography>
        </Box>
        <DepositTextWrapper>
          <Typography variant="body-default" color="text.secondary">
            Deposit {supporttedChainList.join(", ")} to start trading.
          </Typography>
        </DepositTextWrapper>
        <Select
          value={selectedChain}
          onChange={(event) => setSelectedChain(event.target.value as Chain)}
          sx={{ width: "100%", maxWidth: "495px", marginBottom: "16px" }}>
          {sortedConfigs.map((option, index) => {
            return (
              <MenuItem key={`cross-chain-modal-select-${index}`} value={option.id}>
                {option.networkName}
              </MenuItem>
            );
          })}
        </Select>
        <WalletAddressContainer>
          <Typography variant="body-md" color="text.primary">
            {walletAddresses
              ? walletAddresses[crossChainConfigs[selectedChain].walletName]
              : "Loading..."}
          </Typography>
        </WalletAddressContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            maxWidth: "495px",
          }}>
          <RetroButton variant="primary" onClick={handleCopy}>
            <Typography variant="title-h3" sx={{ padding: "12px 21.5px" }}>
              COPY ADDRESS
            </Typography>
          </RetroButton>
        </Box>
      </DepositDetailWrapper>
    </StyledDepositDrawer>
  );
};
